import pdfMake from 'pdfmake/build/pdfmake';
import Logo from 'Icons/RMlogo.png';
import { ExperimentReportOptionsDTO } from './ExperimentReportOptions';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  getParentPath,
  GetIngredients,
  isNullOrUndefined,
  getIngredientStock,
  convertRGBToHex,
} from 'utils/Global';
import {
  ExperimentReportCompositeImageDTO,
  IngredientStockDTO,
  TreeNodeDTO,
} from 'business/models/Models';
import ExperimentService from 'business/services/ExperimentService';
import { randomLayerDetaiSubReport } from './randomLayerDetailSubReport';
import { gridLayerDetailSubReport } from './gridLayerDetailSubReport';
import { customLayerDetailSubReport } from './customLayerDetailSubReport';
import { screenLayerSubReport } from './screenLayerSubReport';
import { proteinLayerSubReport } from './proteinLayersDetailSubReport';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import PlateService from 'business/services/PlateService';
import { cloneDeep } from 'lodash';
import { IngredientReportOptionsDTO, pageSize, fonts } from './ReportsConstants';
import { getPlainExperimentInfo } from 'transformers/experiment';
import { Plate } from 'components/Experiment/constants';
import {
  designTableData as designImageReportTableData,
  getImagesTable,
} from './ImageReportPDF';
import { ImageReportOptionsDTO } from './ImageReportOptions';
import {
  designTableData,
  getWellsTable as ingReportGetWellsTable,
} from './IngredientReportPdf';
import {
  createPdfWithPopupBlockerHandler,
  estimateCharsPerLine,
  estimateRunsLineCount,
  getDesignHeaderRuns,
  getFieldRuns,
  getScreenNameLineRuns,
  getScreenNamesFromLayers,
  ROBOTO_LINE_HEIGHT_RATIO,
  DESIGN_LABEL_FONT_SIZE,
  DESIGN_LABEL_LINE_HEIGHT,
  IMAGE_INFO_FONT_SIZE,
  IMAGE_INFO_FIELD_SEPARATOR,
  INGREDIENT_INFO_FONT_SIZE,
  INGREDIENT_INFO_FIELD_SEPARATOR,
  PDFTextProps,
} from './utils';
pdfMake.fonts = { Roboto: fonts.Roboto };

class ExperimentIngredients {
  name: string;
  volume: number;
  id: number;
  vendorName: string;
  vendorPartNum: string;
  stockNumber: number;

  constructor(ingStock: IngredientStockDTO, longName: string) {
    this.name =
      ingStock.stockConcentration + ' ' + ingStock.concentrationUnits + ' ' + longName;
    if (ingStock.isBuffer) {
      this.name += ' pH ' + ingStock.stockPh;
    }
    this.volume = 0;
    this.id = ingStock.ingredientId;
    this.vendorName = ingStock.vendor ? ingStock.vendor.name : '';
    this.vendorPartNum = ingStock.vendorPartNumber;
    this.stockNumber = ingStock.stockNumber;
  }
}

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
};

const treeService = new TreeService();
const expService = new ExperimentService();
const plateService = new PlateService();

let expParents: TreeNodeDTO[] = [];

export async function generateExperimentReportPDF(
  options: ExperimentReportOptionsDTO,
  exp: ExperimentDtoClient,
  designImages: any[],
  saveReport: boolean,
  onPopupBlocked?: () => void,
) {
  const expData: ExperimentDtoClient = cloneDeep(exp);
  expParents = await treeService.getDocumentParentNodes(
    expData.experimentId,
    ETreeNodeTypes.Experiment,
    true,
  );
  const pageMargins = [20, 30, 20, 10];
  const pdfDefinition = {
    info: {
      title: expData.expName + ' - Experiment Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(options, expData, designImages, pageMargins),
    styles: {
      headerText: { fontSize: 10, bold: true, lineHeight: 1.5 },
      largeText: { fontSize: 10, lineHeight: 1.5 },
      labelText: { fontSize: 8, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 7,
      lineHeight: 1.2,
    },
    pageMargins: pageMargins,
    pageSize: 'A4',
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    expData.expName + ' - Experiment Report',
    saveReport,
    onPopupBlocked,
  );
}

async function CreateContent(
  options: ExperimentReportOptionsDTO,
  expData: ExperimentDtoClient,
  designImages: any[],
  pageMargins: number[],
) {
  const content = [];
  content.push({
    text: expData.expName + ' ( ID: ' + expData.experimentId + ' )',
    style: 'largeText',
  });
  content.push('\n');
  if (options.experiment) {
    content.push({ text: 'Experiment Information', style: 'headerText' });
    content.push(await expInformationTable(expData));
  }
  if (options.projectInfo === true) {
    content.push({ text: '\nProject Information', style: 'headerText' });
    content.push(await getProjectInfoTable(expData.experimentId));
  }
  content.push('\n');

  if (options.experiment) {
    let plateNImagingRow: any = {
      columns: [
        {
          text: options.plates ? 'Experiment Plate(s)' : { text: '', width: '35%' },
          style: 'headerText',
          width: '35%',
        },
        { text: '', width: '25%' },
        {
          text: options.imaging ? 'Experiment Imaging Schedule' : '',
          style: 'headerText',
          width: '35%',
        },
      ],
    };

    content.push(plateNImagingRow);
    plateNImagingRow = {
      columns: [
        options.plates ? getPlatesInfoTable(expData) : { text: '', width: '35%' },
        { text: '', width: '25%' },
        options.imaging ? await getImagingScheduleTable(expData) : '',
      ],
    };
    content.push(plateNImagingRow);
  }

  if (options.requiredStocks) {
    content.push({ text: '\nExperiment Ingredients', style: 'headerText' });
    content.push(getExpIngredientsTable(expData));
  }

  const screenNames = getScreenNamesFromLayers(expData.layers);

  if (options.customLayer) {
    await customLayerDetailSubReport(content, expData);
  }
  if (options.randomLayer) {
    await randomLayerDetaiSubReport(content, expData);
  }

  if (options.gridLayer) {
    await gridLayerDetailSubReport(content, expData);
  }

  if (options.proteinLayer) {
    await proteinLayerSubReport(content, expData);
  }

  if (options.screenLayer) {
    await screenLayerSubReport(content, expData, options.screenDetail);
  }
  if (options.designReport) {
    await getDesignReport(designImages, content, pageMargins, screenNames);
  }

  if (options.dropImages) {
    await getDropImagesReport(expData, options, content, pageMargins, screenNames);
  }

  if (options.wellIngredients) {
    await getWellIngredientsReport(expData, content, pageMargins, screenNames);
  }

  return content;
}

type DesignPlateImage = {
  plateId: number | string;
  plateBarName: string;
  pltImage: string;
};

export function buildDesignPageEntry(
  expPath: string,
  plateImage: DesignPlateImage,
  pageMargins: number[],
  screenNameRuns: PDFTextProps[],
  maxLineChars: number,
  nodeHeadlineLevel: number,
) {
  const availableWidth = pageSize.a4.height;
  const availableHeight = pageSize.a4.width - pageMargins[1] - pageMargins[3];

  const labelRuns = getDesignHeaderRuns(
    expPath,
    plateImage.plateId,
    plateImage.plateBarName.split(',')[2],
    screenNameRuns,
  );
  const labelHeight =
    estimateRunsLineCount(labelRuns, maxLineChars) *
    DESIGN_LABEL_FONT_SIZE *
    DESIGN_LABEL_LINE_HEIGHT *
    ROBOTO_LINE_HEIGHT_RATIO;

  return [
    {
      text: labelRuns,
      alignment: 'left',
      pageBreak: 'before',
      pageOrientation: 'landscape',
      nodeHeadlineLevel: nodeHeadlineLevel,
    },
    {
      image: plateImage.pltImage,
      fit: [availableWidth, availableHeight - labelHeight],
      alignment: 'center',
    },
  ];
}

async function getDesignReport(
  designImages: DesignPlateImage[],
  content: any[],
  pageMargins: number[],
  screenNames: string[],
) {
  const expPath = expParents ? getParentPath(expParents) : '';
  const availableWidth = pageSize.a4.height;
  const labelAvailableWidth = availableWidth - pageMargins[0] - pageMargins[2];
  const maxLineChars = estimateCharsPerLine(labelAvailableWidth, DESIGN_LABEL_FONT_SIZE);
  const screenNameRuns = getScreenNameLineRuns(screenNames, maxLineChars);

  let nodeHeadlineLevel = 0;
  designImages.forEach((plateImage) => {
    nodeHeadlineLevel = nodeHeadlineLevel + 1;
    content.push(
      ...buildDesignPageEntry(
        expPath,
        plateImage,
        pageMargins,
        screenNameRuns,
        maxLineChars,
        nodeHeadlineLevel,
      ),
    );
  });
}

function getDropImageHeaderRuns(
  fields: { label: string; value: any }[],
  screenNameRuns: PDFTextProps[],
) {
  return [
    ...getFieldRuns(fields, IMAGE_INFO_FIELD_SEPARATOR),
    { text: '\n' },
    ...screenNameRuns,
  ];
}

async function getDropImagesReport(
  expData: ExperimentDtoClient,
  options: ExperimentReportOptionsDTO,
  content: any,
  pageMargins: number[],
  screenNames: string[],
) {
  const experimentImageDTO: ExperimentReportCompositeImageDTO =
    new ExperimentReportCompositeImageDTO();
  experimentImageDTO.experimentId = expData.experimentId;
  experimentImageDTO.captureProfiles = [];

  const selectedCaptureProfiles = options.captureProfiles
    .filter((cp) => cp.selected)
    .map((cp) => cp.id);
  experimentImageDTO.captureProfiles = selectedCaptureProfiles;

  const expPath = expParents ? getParentPath(expParents) : '';
  const contentWidth = pageSize.a4.height - pageMargins[0] - pageMargins[2];
  const maxLineChars = estimateCharsPerLine(contentWidth, IMAGE_INFO_FONT_SIZE);
  const screenNameRuns = getScreenNameLineRuns(screenNames, maxLineChars);

  let nodeHeadlineLevel = 0;
  const dropImageTasks: Promise<void>[] = [];

  for (const plate of expData.plateDto) {
    const plateGroupedInspections = [];
    expData.inspections?.forEach((inspectionSet) => {
      inspectionSet.groupedInspections?.forEach((gIns) => {
        if (gIns.plateId === plate.plateId) {
          plateGroupedInspections.push({
            imageBatchId: gIns.imageBatchId,
            dateImaged: inspectionSet.dateImaged,
            inspSetName: inspectionSet.name,
          });
        }
      });
    });

    for (const drop of expData.containerDrops) {
      for (const gIns of plateGroupedInspections) {
        for (const captureProfileId of selectedCaptureProfiles) {
          dropImageTasks.push(
            (async () => {
              const data = await getImagesTable(
                expData,
                plate.plateId,
                drop.dropNumber,
                captureProfileId,
                gIns.imageBatchId,
              );

              if (data.length > 0) {
                nodeHeadlineLevel++;

                content.push({
                  alignment: 'left',
                  pageOrientation: 'landscape',
                  headlineLevel: nodeHeadlineLevel,
                  pageBreak: 'before',
                  text: getDropImageHeaderRuns(
                    [
                      { label: 'Experiment: ', value: expPath },
                      { label: 'Plate ID: ', value: plate.plateId },
                      { label: 'Barcode: ', value: plate.plateBarName.split(',')[2] },
                      { label: 'Drop Number: ', value: drop.dropNumber },
                      {
                        label: 'Inspection Date: ',
                        value:
                          new Date(gIns.dateImaged).toLocaleTimeString() +
                          ' ' +
                          new Date(gIns.dateImaged).toLocaleDateString('en-US'),
                      },
                    ],
                    screenNameRuns,
                  ),
                });

                const reportOptions: ImageReportOptionsDTO = {
                  dropNumber: '',
                  fitToPage: true,
                  columnLimit: expData.containerCols,
                  rowLimit: expData.containerRows,
                  inspection: null,
                };

                const table = await designImageReportTableData(
                  reportOptions,
                  data,
                  expData,
                );
                content.push(table);
              }
            })(),
          );
        }
      }
    }
  }
  await Promise.all(dropImageTasks);
}

function getIngredientHeaderRuns(
  fields: { label: string; value: any }[],
  screenNameRuns: PDFTextProps[],
) {
  return [
    ...getFieldRuns(fields, INGREDIENT_INFO_FIELD_SEPARATOR),
    { text: '\n' },
    ...screenNameRuns,
  ];
}

async function getWellIngredientsReport(
  expData: ExperimentDtoClient,
  content: any,
  pageMargins: number[],
  screenNames: string[],
) {
  const expPath = expParents ? getParentPath(expParents) : '';
  const contentWidth = pageSize.a4.height - pageMargins[0] - pageMargins[2];
  const maxLineChars = estimateCharsPerLine(contentWidth, INGREDIENT_INFO_FONT_SIZE);
  const screenNameRuns = getScreenNameLineRuns(screenNames, maxLineChars);
  const wellIngredientTasks: Promise<void>[] = [];

  for (const plateInfo of expData.plateDto) {
    const hasIngredients = plateInfo.ingredients.wellList.some((well) =>
      well.wellLayerList.some((layer) => layer.wellLayerIngredientList.length > 0),
    );
    if (!hasIngredients) {
      continue;
    }

    const wellInfo = { plateID: plateInfo.plateId };

    let maxIngredients = 0;
    plateInfo.ingredients.wellList.forEach((well) => {
      let currentMax = 0;
      well.wellLayerList.forEach((lyr) => {
        currentMax += lyr.wellLayerIngredientList.length;
      });
      if (currentMax > maxIngredients) {
        maxIngredients = currentMax;
      }
    });

    const checkedDrops = [];
    plateInfo.ingredients.wellList.forEach((well) => {
      well.dropList.forEach((drop) => {
        if (drop.proteinFormulationID) {
          checkedDrops.push(drop.dropNumber);
        }
      });
      // fallback if no drops are added, we still add the first drop to ensure ingredient report is still created
      if (checkedDrops.length === 0) {
        checkedDrops.push(well.dropList.at(0).dropNumber);
      }
    });

    const dropsArr =
      expData.containerDrops?.map((element) => ({
        name: 'drop' + element.dropNumber,
        dropNumber: element.dropNumber,
        checked: checkedDrops.includes(element.dropNumber),
      })) ?? [];

    const ingredientReportOptions: IngredientReportOptionsDTO = {
      dropNumber: '',
      fitToPage: false,
      rowLimit: Math.min(4, plateInfo.rows),
      columnLimit: Math.min(6, plateInfo.cols),
      maxIngredients: Math.min(4, maxIngredients),
      showProteinName: true,
      showColumnNameHeading: true,
      showDropVolumes: true,
      showWellVolumes: true,
      concentration: true,
      pH: true,
      pHVolumes: true,
      ingredientNamePreference: 'longName',
      drops: dropsArr,
      fontFamily: 'Roboto',
      fontStyle: 'Regular',
      fontSize: 12,
      showFragmentName: true,
      showSeedName: true,
      showAdditiveName: true,
      isScreenExperiment: !!expData.containerDrops,
    };

    for (const drop of ingredientReportOptions.drops) {
      if (!drop.checked) {
        continue;
      }

      wellIngredientTasks.push(
        (async () => {
          const data = await ingReportGetWellsTable(
            ingredientReportOptions,
            drop.dropNumber,
            expData,
            plateInfo.plateId,
            wellInfo,
          );

          if (data.length > 0) {
            content.push({
              text: getIngredientHeaderRuns(
                [
                  { label: 'Experiment: ', value: expPath },
                  { label: 'Plate ID: ', value: plateInfo.plateId },
                  { label: 'Barcode: ', value: plateInfo.plateBarName.split(',')[2] },
                  { label: 'Drop Number: ', value: drop.dropNumber },
                ],
                screenNameRuns,
              ),
              alignment: 'center',
              pageBreak: 'before',
              pageOrientation: 'landscape',
            });

            content.push('\n');
            content.push(await designTableData(ingredientReportOptions, data, expData));
          } else {
            content.push({
              text: 'The document does not contain any pages.',
              alignment: 'center',
              fontSize: 12,
              color: 'grey',
            });
          }
        })(),
      );
    }
  }
  await Promise.all(wellIngredientTasks);
}

function getExpIngredientsTable(expData: ExperimentDtoClient) {
  const body: any = [
    [
      { text: 'Ingredient', bold: true },
      { text: 'ID', bold: true },
      { text: 'Stock Number', bold: true },
      { text: 'Total Volume (μl)', bold: true },
      { text: 'Vendor Name', bold: true },
      { text: 'Vendor Part Number', bold: true },
    ],
  ];

  const expIngredients = getExperimentIngredients(expData);
  expIngredients.forEach((ing) => {
    body.push([
      ing.name,
      ing.id,
      ing.stockNumber,
      ing.volume,
      ing.vendorName,
      ing.vendorPartNum,
    ]);
  });

  return {
    table: {
      widths: ['22%', '5%', '15%', '18%', '20%', '20%'],
      headerRows: 1,
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getImagingScheduleTable(expData: ExperimentDtoClient) {
  const tempBody = [];
  const body: any = [
    [
      { text: 'Days', bold: true },
      { text: 'Hours', bold: true },
    ],
  ];
  if (isNullOrUndefined(expData.imagingSchedule)) {
    expData.imagingSchedule = await expService.getImagingSchedule(expData.experimentId);
  }
  expData.imagingSchedule.imagingScheduleEntries.forEach((entry) => {
    if (entry.id === expData.imagingSchedule.selectedImagingScheduleId) {
      if (!tempBody.some((tb) => tb.entryId == entry.entryId)) {
        tempBody.push(entry);
        body.push([entry.days.toString(), entry.hours.toString()]);
      }
    }
  });
  return {
    width: '35%',
    table: {
      widths: ['50%', '50%'],
      headerRows: 1,
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

function getPlatesInfoTable(expData: ExperimentDtoClient) {
  const body: any = [
    [
      { text: 'Plate ID', bold: true },
      { text: 'Plate#', bold: true },
      { text: 'Barcode', bold: true },
    ],
  ];
  expData.plateDto.forEach((p) => {
    body.push([
      { text: p.plateId },
      { text: p.plateNumber },
      { text: p.plateBarName.split(',')[2] },
    ]);
  });
  return {
    width: '35%',
    table: {
      widths: ['33%', '33%', '34%'],
      headerRows: 1,
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getProjectInfoTable(expId: number) {
  const projectInfo = await treeService.getExpProjectInfo(expId);
  return {
    table: {
      widths: ['15%', '35%'],
      body: [
        [{ text: 'Name: ', bold: true }, { text: projectInfo.name }],
        [
          { text: 'Date Created: ', bold: true },
          { text: projectInfo.dateCreated.toLocaleString('en-US') },
        ],
        [{ text: 'Owner: ', bold: true }, { text: projectInfo.owner }],
        [{ text: 'Notes: ', bold: true }, { text: projectInfo.notes }],
      ],
    },
    layout: TABLE_LAYOUT,
  };
}

async function expInformationTable(expData: ExperimentDtoClient) {
  const expInfoPromise = async () => {
    if (isNullOrUndefined(expData.info)) {
      expData.info = getPlainExperimentInfo(
        await expService.getExperimentInfo(expData.experimentId),
      );
    }
  };
  const expPathPromise = async () => {
    return expParents;
  };

  return await Promise.all([expInfoPromise(), expPathPromise()]).then(() => {
    return {
      style: 'table',
      table: {
        widths: ['20%', '40%', '20%', '20%'],
        body: [
          [
            { text: 'Experiment Path', bold: true },
            { text: getParentPath(expParents), colSpan: '3' },
            '',
            '',
          ],
          [
            { text: 'Date Created: ', bold: true },
            { text: expData.info.dateCreated },
            { text: 'Setup Temprature', bold: true },
            { text: expData.info.setupTemperature + ' °C' },
          ],
          [
            { text: 'Experiment Owner: ', bold: true },
            { text: expData.info.userName },
            { text: 'Incubation Temprature', bold: true },
            { text: expData.info.incubTemperature + ' °C' },
          ],
          [
            { text: 'Plate Type: ', bold: true },
            { text: expData.info.plateType },
            { text: 'Reducing Agent Volume', bold: true },
            { text: expData.info.reducingAgent + ' µl' },
          ],
          [
            { text: 'Plate Rows x Columns: ', style: 'tableSubHeader' },
            { text: expData.rowCount + ' x ' + expData.colCount },
            { text: 'Seeding', style: 'tableSubHeader' },
            { text: expData.info.seeding ? 'Yes' : 'No' },
          ],
          [
            { text: 'Notebook Page: ', style: 'tableSubHeader' },
            { text: expData.info.noteBookPageNum },
            { text: 'Dispensing Robot', bold: true },
            { text: expData.info.isDispensed ? 'Yes' : 'No' },
          ],
          [
            { text: 'Notes', style: 'tableSubHeader' },
            { text: expData.info.notes, colSpan: '3' },
            '',
            '',
          ],
        ],
      },
      layout: TABLE_LAYOUT,
    };
  });
}

export function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
          alignment: 'right',
        },
      ],
      margin: [20, 5, 20, 20],
    },
  ];
}

function getExperimentIngredients(docData: ExperimentDtoClient) {
  const ingredientStocksMap = new Map<number, ExperimentIngredients>();
  const waterStockId = GetIngredients().ingredients.find(
    (ing) => ing.longName === 'Water',
  ).defaultNonBufferIngredientStockId;
  const waterStock = GetIngredients().ingredientStock.find(
    (stock) => stock.id === waterStockId,
  );
  const waterIng = new ExperimentIngredients(waterStock, 'Water');
  ingredientStocksMap.set(waterStockId, waterIng);

  docData.plateDto.forEach((plate) => {
    plate.ingredients.wellList.forEach((well) => {
      if (!isNullOrUndefined(well.wellWaterVolume)) {
        const water = ingredientStocksMap.get(waterStockId);
        water.volume += well.wellWaterVolume;
        well.wellLayerList.forEach((wellLayer) => {
          wellLayer.wellLayerIngredientList.forEach((wellIng) => {
            let expIng = ingredientStocksMap.get(wellIng.ingredientStockId);
            if (isNullOrUndefined(expIng)) {
              const ingStock = getIngredientStock(wellIng.ingredientStockId);
              expIng = new ExperimentIngredients(
                ingStock,
                wellIng.ingredientStockIngredient.longName,
              );
              ingredientStocksMap.set(wellIng.ingredientStockId, expIng);
            }
            if (wellIng.ingredientTypeIsBuffer) {
              expIng.volume += wellIng.lowPHVolume;
              if (!isNullOrUndefined(wellIng.highPhIngStock)) {
                expIng.volume += wellIng.highPHVolume;
              }
            } else {
              expIng.volume += wellIng.volume;
            }
          });
        });
      }
    });
  });
  return ingredientStocksMap;
}

export async function getWellsTable(
  options: IngredientReportOptionsDTO,
  dropNumber: number,
  expData: ExperimentDtoClient,
  plateInfo: Plate,
  wellInfo: any,
) {
  const rows: any = [];
  let columns: any = [];
  const wellData = await getWellData(expData, wellInfo, dropNumber);
  const plateScores = await plateService.getPlateScoreBasedOnWellsByPlateId(
    plateInfo.plateId,
  );

  let width;
  let rowHeight;
  let tableHeight;
  if (options.rowLimit > options.columnLimit) {
    tableHeight = 650;
    width = 500 / options.columnLimit;
    rowHeight = 800 / options.rowLimit;

    if (rowHeight < width) {
      width = rowHeight;
    }
  } else {
    width = 760 / options.columnLimit;
    rowHeight = 500 / options.rowLimit;

    if (rowHeight < width) {
      width = rowHeight;
    }

    tableHeight = 420;
  }

  let dropImageCount = 0;
  const columnLoopCount = expData.containerCols + 1;
  for (let i = 0; i < expData.containerRows; i++) {
    for (let j = 0; j < columnLoopCount; j++) {
      if (j === 0) {
        columns.push([
          {
            width: 20,
            text: String.fromCharCode('A'.charCodeAt(0) + i),
            alignment: 'center',
            margin: [0, tableHeight / options.rowLimit / 2 - 10, 0, 0],
            border: [false, false, false, false],
          },
        ]);
      } else {
        const wellScores: any[] = [];
        if (plateScores.length > 0 && expData.expSelectedStates?.inspectionSetId) {
          const inspectionSet: any = expData.inspections.find(
            (insp) => insp.id == expData.expSelectedStates?.inspectionSetId,
          );
          const imageBatchId: number = inspectionSet.groupedInspections.find(
            (gi) => gi.plateId == plateInfo.plateId,
          )?.imageBatchId;
          plateScores.forEach((scores) => {
            scores.forEach((wellScore) => {
              if (
                wellScore.wellNumber == wellData[dropImageCount].wellNumber &&
                wellScore.dropNumber == dropNumber &&
                wellScore.scoreBatchId == imageBatchId
              ) {
                wellScores.push(wellScore);
              }
            });
          });
        }
        const scoresBody: any[] = [];
        const scoreTextArray: any[] = [];
        scoresBody.push({
          text: '',
          border: [false, false, false, false],
        });

        scoreTextArray.push(' ');
        wellScores.forEach((wellScore) => {
          let scoreText = '';

          if (wellScore.isAutoScore) {
            if (wellScore.autoScoreValue == 1) {
              scoreText = '+';
            } else if (wellScore.autoScoreValue == 0) {
              scoreText = '-';
            } else if (wellScore.autoScoreValue == null) {
              scoreText = ' ';
            } else {
              scoreText = wellScore.autoScoreValue.toFixed(2);
            }
          } else {
            scoreText = wellScore.hotKey;
          }
          scoresBody.push({
            text: scoreText,
            alignment: 'center',
            fillColor: convertRGBToHex(wellScore.colorRGB),
            color: 'white',
            border: [false, false, false, false],
          });
          scoreTextArray.push(scoreText);
        });
        const scoresWidth: any[] = [];
        for (let i = 0; i < scoresBody.length; i++) {
          if (i == 0) {
            scoresWidth.push('*');
          } else {
            if (scoreTextArray[i].length == 1) {
              scoresWidth.push('15%');
            } else {
              scoresWidth.push('auto');
            }
          }
        }

        let columnCount = 0;
        const ingredientDataOptions: any = [];
        if (options.concentration) {
          columnCount = columnCount + 1;
          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'C' : 'Conc',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });
        }
        if (options.pHVolumes) {
          columnCount = columnCount + 2;
          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'L' : 'L Vol',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });

          ingredientDataOptions.push({
            text: options.columnLimit > 6 ? 'H' : 'H Vol',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });
        }
        if (options.pH) {
          columnCount = columnCount + 1;
          ingredientDataOptions.push({
            text: 'pH',
            bold: true,
            fillColor: '#808080',
            color: '#FFFFFF',
            alignment: 'center',
          });
        }

        let rowCount = 0;
        const body: any[] = [];

        const headerArray: any[] = [];
        headerArray.push({
          columns: [
            {
              width: '70%',
              text:
                'Plate ID ' +
                wellData[dropImageCount].plateId +
                ', ' +
                wellData[dropImageCount].wellName +
                (wellData[dropImageCount].dropNumber
                  ? '-' + wellData[dropImageCount].dropNumber
                  : ''),
              bold: true,
            },
            {
              width: '*',
              margin: [-3, 0, -3, 0],
              table: {
                widths: scoresWidth,
                body: [scoresBody],
              },
              layout: {
                hLineWidth: function hLineWidth(i) {
                  return 0.1;
                },
                vLineWidth: function vLineWidth(i) {
                  return 0.1;
                },
                paddingLeft: function (i, node) {
                  return 0;
                },
                paddingRight: function (i, node) {
                  return 0;
                },
                paddingTop: function (i, node) {
                  return 0;
                },
                paddingBottom: function (i, node) {
                  return 0;
                },
              },
            },
          ],
          colSpan: columnCount,
          border: [true, false, true, false],
        });

        for (let i = 1; i < columnCount; i++) {
          headerArray.push({});
        }
        body.push(headerArray);
        rowCount = rowCount + 1;

        if (options.showProteinName) {
          const proteinNameArray: any[] = [];
          proteinNameArray.push({
            text: wellData[dropImageCount].proteinFormulationName
              ? 'Protein: ' + wellData[dropImageCount].proteinFormulationName
              : '',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            proteinNameArray.push({});
          }

          body.push(proteinNameArray);
          rowCount = rowCount + 1;
        }

        if (options.showDropVolumes) {
          const drop = wellData[dropImageCount];

          const parts = [
            drop?.dropProteinVolume && `${drop.dropProteinVolume} μL p`,
            drop?.dropWellVolume && `${drop.dropWellVolume} μL w`,
            drop?.dropSeedVolume && `${drop.dropSeedVolume} μL s`,
            drop?.dropFragment && `${drop.dropFragment} μL f`,
          ].filter(Boolean);

          const result = `Drop: ${parts.join(' + ')}`;
          const dropVolumesArray: any[] = [];
          dropVolumesArray.push({
            text: drop?.proteinFormulationName ? result : ' ',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            dropVolumesArray.push({});
          }

          body.push(dropVolumesArray);
          rowCount = rowCount + 1;
        }

        if (options.showWellVolumes) {
          const wellVolumeArray: any[] = [];
          wellVolumeArray.push({
            text:
              wellData[dropImageCount].wellIngredientList.length > 0
                ? 'Well:' + wellData[dropImageCount].wellVolume + 'μL'
                : ' ',
            colSpan: columnCount,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            wellVolumeArray.push({});
          }

          body.push(wellVolumeArray);
          rowCount = rowCount + 1;
        }

        if (wellData[dropImageCount].wellIngredientList.length > 0) {
          if (options.showColumnNameHeading) {
            body.push(ingredientDataOptions);
            rowCount = rowCount + 1;
          }

          let highlightRow = false;

          for (let i = 0; i < options.maxIngredients; i++) {
            const ingredient = wellData[dropImageCount].wellIngredientList[i];
            const ingLongName = ingredient?.ingredientStockIngredient.longName;
            const ingShortName = ingredient?.ingredientStockIngredient.shortName;
            const ingConcentration = ingredient?.concentration;
            const ingpH = ingredient?.pH;
            const lowVolume = ingredient?.isBuffer
              ? ingredient?.lowPHVolume
              : ingredient?.volume;
            const highVolume = ingredient?.highPHVolume;

            let fillColor;
            if (highlightRow) {
              fillColor = '#D3D3D3';
            } else {
              fillColor = '#FFFFFF';
            }
            const ingNameArray: any = [];
            ingNameArray.push({
              text: ingredient
                ? options.ingredientNamePreference == 'longName'
                  ? options.columnLimit >= 6
                    ? ingShortName
                    : ingLongName
                  : ingShortName
                : ' ',
              colSpan: columnCount,
              alignment: 'center',
              fillColor: fillColor,
              border: [true, true, true, true],
            });

            for (let i = 1; i < columnCount; i++) {
              ingNameArray.push({});
            }

            body.push(ingNameArray);

            const ingDataArray: any = [];

            if (options.concentration) {
              ingDataArray.push({
                text: ingConcentration ? Number(ingConcentration?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }
            if (options.pHVolumes) {
              ingDataArray.push({
                text: lowVolume ? Number(lowVolume?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });

              ingDataArray.push({
                text: highVolume ? Number(highVolume?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }
            if (options.pH) {
              ingDataArray.push({
                text: ingpH ? Number(ingpH?.toFixed(1)) : null,
                fillColor: fillColor,
                border: [true, true, true, true],
                alignment: 'center',
              });
            }

            body.push(ingDataArray);

            rowCount = rowCount + 2;
            highlightRow = !highlightRow;
          }
        } else {
          const fillColor = '#FFFFFF';

          const headerArray: any = [];
          headerArray.push({
            text: 'header',
            colSpan: columnCount,
            alignment: 'center',
            fillColor: fillColor,
            color: fillColor,
            border: [true, false, true, false],
          });

          for (let i = 1; i < columnCount; i++) {
            headerArray.push({});
          }
          body.push(headerArray);
          rowCount = rowCount + 1;

          for (let i = 0; i < options.maxIngredients; i++) {
            for (let j = 0; j < 2; j++) {
              const ingArray: any[] = [];
              ingArray.push({
                text: 'header',
                colSpan: columnCount,
                alignment: 'center',
                fillColor: fillColor,
                color: fillColor,
                border: [true, false, true, false],
              });

              for (let i = 1; i < columnCount; i++) {
                ingArray.push({});
              }
              body.push(ingArray);
              rowCount = rowCount + 1;
            }
          }
        }

        const columnWidths = [];
        for (let i = 0; i < columnCount; i++) {
          columnWidths.push((100 / columnCount).toString() + '%');
        }

        let fontSize = 15;
        if (options.columnLimit >= options.rowLimit) {
          for (let i = 0; i < options.columnLimit; i++) {
            fontSize = fontSize - 1;
          }

          const rowHeight = tableHeight / options.rowLimit / rowCount;
          if (rowHeight - fontSize < 2) {
            fontSize = rowHeight * 0.6;
          }
        } else {
          for (let i = 0; i < options.rowLimit; i++) {
            fontSize = fontSize - 1;
          }

          for (let i = 0; i < options.columnLimit; i++) {
            fontSize = fontSize - 0.3;
          }

          for (let i = 1; i < options.maxIngredients; i++) {
            fontSize = fontSize * 0.9;
          }
        }

        columns.push([
          {
            stack: [
              {
                fontSize: fontSize,
                margin: [-5, -2, -5, -2],
                table: {
                  heights: tableHeight / options.rowLimit / rowCount,
                  widths: columnWidths,
                  body: body,
                },
                layout: {
                  hLineWidth: function hLineWidth(i) {
                    return 0.1;
                  },
                  vLineWidth: function vLineWidth(i) {
                    return 1;
                  },
                  paddingTop: function (i, node) {
                    const text = node.table.body[i][0]._inlines;
                    if (text && text.length > 0) {
                      const textHeight = text[0].height;
                      const margin = (node.table.heights - textHeight) / 2;
                      node.table.body[i].forEach((cell) => {
                        if (cell._margin) {
                          cell._margin[1] = margin;
                        } else {
                          cell._margin = [0, margin, 0, 0];
                        }
                      });
                    }
                    return 0;
                  },
                  paddingBottom: function (i, node) {
                    return 0;
                  },
                },
              },
            ],
          },
        ]);
        dropImageCount++;
      }
    }
    rows.push({
      alignment: 'center',
      columns: columns,
    });
    columns = [];
  }
  return rows;
}

async function getWellData(
  expData: ExperimentDtoClient,
  wellInfo: any,
  dropNumber: number,
) {
  const plateData = expData.plateDto.find((plt) => plt.plateId == wellInfo.plateID);
  const wellList: any[] = [];
  plateData.ingredients.wellList.forEach((well) => {
    const wellData: any = {};
    wellData.wellName = well.rowLetter + well.columnNumber.toString();
    wellData.wellNumber = well.wellNumber;
    wellData.plateId = plateData.plateId;
    wellData.wellVolume = well.wellVolume;
    wellData.dropNumber = dropNumber > 0 ? dropNumber : null;

    let dropData: any = {};
    dropData = well.dropList.find((drp) => drp.dropNumber == dropNumber);

    if (dropData && dropData.proteinFormulationID) {
      wellData.dropProteinVolume = dropData?.dropProteinVolume;
      wellData.dropWellVolume = dropData?.dropWellVolume;
      wellData.dropSeedVolume = dropData?.dropSeedVolume;
      wellData.dropVolume = dropData?.volume;
      wellData.proteinFormulationName =
        plateData.ingredients.proteinFormulation[
          dropData.proteinFormulationID
        ].proteinInfo.name;
    } else {
      wellData.dropProteinVolume = undefined;
      wellData.dropWellVolume = undefined;
      wellData.dropSeedVolume = undefined;
      wellData.dropVolume = undefined;
      wellData.proteinFormulationName = undefined;
    }
    wellData.wellIngredientList = [];

    well.wellLayerList.forEach((wellLyr) => {
      wellLyr.wellLayerIngredientList.forEach((ing) => {
        wellData.wellIngredientList.push(ing);
      });
    });

    wellList.push(wellData);
  });

  return wellList;
}
